import { ChangeDetectorRef, Component, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AprService } from 'app/modules/annulus-pressure-record/services/annulus-pressure-record.service';
import { ActivatedRoute, Router } from '@angular/router';
import { saveAs } from 'file-saver';
import module from "app/modules/annulus-pressure-record/annulus-pressure-record.module";
import { DEFAULT_COLUMN_LABELS } from 'app/core/shared/constant/column-label';
import { AprSearchStateService } from '../../state/search.state';
import { ApplySortPayload } from 'app/core/shared/component/sort-menu/sort-menu.component';

const conversionToPsi = 0.145038 // 1kPa = 0.145038psi, refer to golbal conversion

@Component({
  selector: 'app-well-production-status',
  templateUrl: './index.page.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: module,
  styleUrls: ['./index.page.scss']
})

export class AprIndexPage {
  searchForm: UntypedFormGroup;
  dt: any//Datatable<any>;
  startingIndex: number = 0
  public selectedActType: string = 'General'

  sortInfo: string;
  private columnLabels: { [key: string]: string } = {
    ...DEFAULT_COLUMN_LABELS,
    prodStatus: ' Production Status',
    activity: 'Activity Type',
    recordDate: 'Activity Date',
    thp: 'THP',
    annAPress: 'Annulus Pressure Reading (PCP)',
  };

  columnSort = Object.entries(this.columnLabels).map(([key, name]) => ({
    key,
    name,
    isChecked: false, // default unchecked
  }));

  constructor(
    private aprService: AprService,
    public router: Router,
    private _formBuilder: UntypedFormBuilder,
    private state: AprSearchStateService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit() {
    this.setSearch();
    this.setDataTable();

    const qp = this.route.snapshot.queryParamMap;
    const rawTab = qp.get('tab') ?? null;
    const tab = rawTab && rawTab.toLowerCase().includes('pi') ? 'pi' : rawTab;

    if (tab) this.toggleActType(tab)

    this.setPage({ offset: 0 });
  }

  downloadOverallApr() {
    if (this.selectedActType == 'pi') {
      this.aprService.downloadOverallAprPi().subscribe((res) => {
        saveAs(res, `Annulus Pressure Records (${this.selectedActType})`);
      })
    } else {
      this.aprService.downloadOverallApr(this.selectedActType).subscribe((res) => {
        saveAs(res, `Annulus Pressure Records (${this.selectedActType})`);
      })
    }

  }

  toggleActType(type: string) {
    this.selectedActType = type
    this.sortInfo = type == 'pi' ? 'Activity Date' : 'Field'

    this.setDataTable()
    this.searchForm.get('OrderBy1').setValue(null)
    this.searchForm.get('OrderBy2').setValue(null)
    this.searchForm.get('OrderBy3').setValue(null)
    this.setPage({ offset: 0 });
  }

  // 
  // Method for Ngx-DataTable
  // 

  handleSearch(event$) {
    const field = event$?.Field;
    const platform = event$?.Platform;
    const well = event$?.Well;
    const string = event$?.String;

    if ([field, platform, well, string].some(v => v != null && v !== '')) {
      this.setPage({ offset: 0 });
    }
  }

  setSearch() {
    this.sortInfo = this.selectedActType == 'pi' ? 'Activity Date' : 'Field'
    this.searchForm = this._formBuilder.group({
      Page: [0],
      PageSize: [20],
      OrderBy1: [null],
      OrderBy2: [null],
      OrderBy3: [null],
      OrderDir: 'asc',
      WellName: [null],
      PlatformName: [null],
      StringName: [null],
      FieldName: [null],
    });

    this.searchForm.get('PageSize').valueChanges.subscribe(value => this.setSize(value));
  }

  setSize(size: number) {
    this.dt.page.size = size;
    this.dt.page.offset = 0

    this.setPage({ offset: this.dt.page.offset });
  }

  onSort(sort: any) {
    if (!sort) return;

    this.searchForm.patchValue({
      OrderBy1: sort.active,
      OrderDir: sort.direction,
    });

    this.sortInfo = this.columnLabels[sort.active] || sort.active;
    this.setPage({ offset: 0 });
  }

  setPage(pageInfo) {
    const saved = this.state.get()
    let params: any = {}

    // if have saved search
    if (saved) {
      // is search for changes
      const useSaved = !this.searchForm.dirty;

      this.searchForm.patchValue(
        useSaved ? saved : { Page: pageInfo.offset + 1 },
        { emitEvent: !useSaved }
      );

      this.dt.page.offset = pageInfo.offset;
      this.startingIndex = this.dt.page.offset * (useSaved ? saved.PageSize : this.dt.page.size);
      params = useSaved ? saved : this.searchForm.value;

    } else {
      this.dt.page.offset = pageInfo.offset;
      this.searchForm.patchValue({ Page: this.dt.page.offset + 1 });
      this.startingIndex = this.dt.page.offset * this.dt.page.size;
      params = this.searchForm.value;
    }

    const search: any = {
      wellLongName: params.Well?.wellLongName,
      stringShortName: params.String?.stringShortName,
      platformLongName: params.Platform?.platformLongName,
      fieldLongName: params.Field?.fieldLongName,
      activity: this.selectedActType
    };

    if (this.selectedActType === 'pi') {
      search.activity = '';
    }

    const serviceMethod = this.selectedActType === 'pi'
      ? this.aprService.getAprPiRecords
      : this.aprService.getAprRecords;

    serviceMethod.call(this.aprService, { ...params, search })
      .subscribe((pagedData) => {
        let data = pagedData.data.data

        data.forEach(item => {
          if(item.thp && item.thp as number) {item.thpPsi = item.thp * conversionToPsi} 
          else item.thpPsi = item.thp

          if(this.selectedActType === 'pi' && item.annAPress && item.annAPress as number) {
            item.annAPressPsi = item.annAPress * conversionToPsi
          } else item.annAPressPsi = item.annAPress
        })

        this.dt.page.totalPages = pagedData.data.totalPages;
        this.dt.page.totalRecords = pagedData.data.totalRecords;
        this.dt.rows = pagedData.data.data;
      });
  }

  setDataTable() {
    this.dt = {
      page: {
        size: this.searchForm.get('PageSize')?.value,
        totalRecords: 0,
        totalPages: 0,
        offset: 0,
      },
      rows: [],
    };
  }

  reset() {
    this.state.clear(); // clear session storage
    this.setSearch();
    this.setPage({ offset: 0 });
  }

  applySort(payload: ApplySortPayload) {
    // Patch form (fill missing with null)
    const [OrderBy1, OrderBy2, OrderBy3] = [
      payload.columns[0]?.key ?? null,
      payload.columns[1]?.key ?? null,
      payload.columns[2]?.key ?? null,
    ];

    this.searchForm.patchValue({
      OrderBy1,
      OrderBy2,
      OrderBy3,
      OrderDir: payload.orderDir ?? 'asc',
    });

    const labels = payload.columns.map(k => k.name);
    this.sortInfo = labels.length ? labels.join(' → ') : 'No sorting';
    this.setPage({ offset: 0 });
  }

}
